import { projectsProjectAccessDecisionSchema, } from '@crest/module-projects/contracts/project-access-decision';
import { projectsProjectReferenceSnapshotSchema, } from '@crest/module-projects/contracts/project-reference';
import { z } from 'zod';
import { scanningMediaBoundaryCorrectionSchema } from './scanning-media-boundary-correction.contract.js';
import { scanningMediaSemanticSnapshotSchema, scanningMediaSourceSnapshotSchema, scanningMediaStableKeySchema, } from './scanning-media-semantic-geometry.contract.js';
export const scanningMediaScanCapturesCapability = 'scanning-media.scan-captures.v1';
export const scanningMediaQualityEventsCapability = 'scanning-media.quality-events.v1';
export const scanningMediaCaptureStatusSchema = z.enum(['draft', 'reviewed', 'archived']);
export const scanningMediaSemanticRoleSchema = z.enum(['raw', 'corrected']);
export const scanningMediaScanCaptureVersionSchema = z.strictObject({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    captureId: z.string().uuid(),
    versionNumber: z.number().int().positive(),
    semanticRole: scanningMediaSemanticRoleSchema,
    sourceVersionId: z.string().uuid().nullable(),
    snapshot: scanningMediaSemanticSnapshotSchema,
    contentHash: z.string().regex(/^[a-f0-9]{64}$/),
    reason: z.string().trim().min(10).max(500),
    createdByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
});
export const scanningMediaQualityEventTypeSchema = z.enum([
    'capture-advisory',
    'geometry-validation',
    'measurement-validation',
    'backend-acceptance',
]);
export const scanningMediaClientQualityEventTypeSchema = z.enum([
    'capture-advisory',
    'geometry-validation',
    'measurement-validation',
]);
export const scanningMediaQualityEventSeveritySchema = z.enum(['info', 'warning', 'error']);
export const scanningMediaQualityEventSourceSchema = z.enum([
    'device-advisory',
    'operator-review',
    'server-acceptance',
]);
export const scanningMediaQualityEventDetailsSchema = z.record(z.string().min(1).max(80), z.union([z.string().max(500), z.number().finite(), z.boolean(), z.null()]));
export const scanningMediaQualityEventSchema = z.strictObject({
    contractVersion: z.literal(scanningMediaQualityEventsCapability),
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    captureId: z.string().uuid(),
    clientEventId: z.string().uuid().nullable(),
    type: scanningMediaQualityEventTypeSchema,
    severity: scanningMediaQualityEventSeveritySchema,
    source: scanningMediaQualityEventSourceSchema,
    code: scanningMediaStableKeySchema,
    message: z.string().trim().min(1).max(500),
    floorKey: scanningMediaStableKeySchema.nullable(),
    roomKey: scanningMediaStableKeySchema.nullable(),
    details: scanningMediaQualityEventDetailsSchema,
    occurredAt: z.string().datetime({ offset: true }),
    receivedAt: z.string().datetime({ offset: true }),
    recordedByMembershipId: z.string().uuid(),
});
export const scanningMediaScanCaptureSchema = z.strictObject({
    contractVersion: z.literal(scanningMediaScanCapturesCapability),
    tenantId: z.string().uuid(),
    id: z.string().uuid(),
    captureUid: z.string().uuid(),
    projectReference: projectsProjectReferenceSnapshotSchema,
    projectAccessDecision: projectsProjectAccessDecisionSchema,
    status: scanningMediaCaptureStatusSchema,
    revision: z.number().int().positive(),
    latestVersionId: z.string().uuid(),
    acceptedVersionId: z.string().uuid().nullable(),
    archivedAt: z.string().datetime({ offset: true }).nullable(),
    createdByMembershipId: z.string().uuid(),
    updatedByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    latestVersion: scanningMediaScanCaptureVersionSchema,
    acceptedVersion: scanningMediaScanCaptureVersionSchema.nullable(),
    qualityEvents: z.array(scanningMediaQualityEventSchema),
}).superRefine((capture, context) => {
    if (capture.status === 'reviewed' && capture.acceptedVersionId === null) {
        context.addIssue({
            code: 'custom', path: ['acceptedVersionId'],
            message: 'A reviewed capture requires an accepted semantic version.',
        });
    }
    if ((capture.status === 'archived') !== (capture.archivedAt !== null)) {
        context.addIssue({
            code: 'custom', path: ['archivedAt'],
            message: 'Archived status and archivedAt must agree.',
        });
    }
});
export const scanningMediaScanListQuerySchema = z.strictObject({
    includeArchived: z.boolean().default(false),
    limit: z.number().int().min(1).max(100).default(25),
    offset: z.number().int().nonnegative().default(0),
});
const scanningMediaCommandBaseSchema = z.strictObject({
    commandId: z.string().uuid(),
    captureId: z.string().uuid(),
    reason: z.string().trim().min(10).max(500),
});
export const scanningMediaCreateScanCommandSchema = scanningMediaCommandBaseSchema.extend({
    kind: z.literal('create'),
    captureUid: z.string().uuid(),
    snapshot: scanningMediaSourceSnapshotSchema,
});
export const scanningMediaReplaceSnapshotCommandSchema = scanningMediaCommandBaseSchema.extend({
    kind: z.literal('replace-snapshot'),
    expectedRevision: z.number().int().positive(),
    snapshot: scanningMediaSourceSnapshotSchema,
});
export const scanningMediaAcceptVersionCommandSchema = scanningMediaCommandBaseSchema.extend({
    kind: z.literal('accept-version'),
    expectedRevision: z.number().int().positive(),
    versionId: z.string().uuid(),
});
export const scanningMediaCorrectBoundaryCommandSchema = scanningMediaCommandBaseSchema.extend({
    kind: z.literal('correct-boundary'),
    expectedRevision: z.number().int().positive(),
    correction: scanningMediaBoundaryCorrectionSchema,
});
export const scanningMediaCaptureStateCommandSchema = scanningMediaCommandBaseSchema.extend({
    kind: z.enum(['archive', 'restore']),
    expectedRevision: z.number().int().positive(),
});
export const scanningMediaScanCommandSchema = z.discriminatedUnion('kind', [
    scanningMediaCreateScanCommandSchema,
    scanningMediaReplaceSnapshotCommandSchema,
    scanningMediaCorrectBoundaryCommandSchema,
    scanningMediaAcceptVersionCommandSchema,
    scanningMediaCaptureStateCommandSchema,
]);
export const scanningMediaRecordQualityEventSchema = z.strictObject({
    clientEventId: z.string().uuid(),
    type: scanningMediaClientQualityEventTypeSchema,
    severity: scanningMediaQualityEventSeveritySchema,
    source: z.enum(['device-advisory', 'operator-review']),
    code: scanningMediaStableKeySchema,
    message: z.string().trim().min(1).max(500),
    floorKey: scanningMediaStableKeySchema.nullable().default(null),
    roomKey: scanningMediaStableKeySchema.nullable().default(null),
    details: scanningMediaQualityEventDetailsSchema.default({}),
    occurredAt: z.string().datetime({ offset: true }),
});
const scanningMediaCommandMetadataSchema = z.strictObject({
    commandId: z.string().uuid(),
    idempotent: z.boolean(),
});
export const scanningMediaScanCaptureResponseSchema = z.strictObject({
    data: scanningMediaScanCaptureSchema,
    command: scanningMediaCommandMetadataSchema.optional(),
});
export const scanningMediaScanCapturesResponseSchema = z.strictObject({
    data: z.array(scanningMediaScanCaptureSchema),
    page: z.strictObject({
        limit: z.number().int().positive(),
        offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
    }),
});
export const scanningMediaScanVersionsResponseSchema = z.strictObject({
    data: z.array(scanningMediaScanCaptureVersionSchema),
});
export const scanningMediaQualityEventResponseSchema = z.strictObject({
    data: scanningMediaQualityEventSchema,
    command: z.strictObject({ clientEventId: z.string().uuid(), idempotent: z.boolean() }),
});
//# sourceMappingURL=scanning-media-scan-capture.contract.js.map